import * as React from "react";
import { ArrowDownIcon, ArrowUpIcon } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";

import { Button, type ButtonProps } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type TableSortDirection = "ascending" | "descending";

const TableSortDirectionContext = React.createContext<
  TableSortDirection | undefined
>(undefined);

const tableVariants = cva("w-full caption-bottom", {
  variants: {
    variant: {
      default:
        "text-sm [--table-cell-padding:0.5rem] [--table-edge-padding:1rem] [--table-row-height:2.5rem]",
      compact:
        "text-xs [--table-cell-padding:0.25rem] [--table-edge-padding:0.5rem] [--table-row-height:1.75rem]",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

interface TableProps
  extends
    React.HTMLAttributes<HTMLTableElement>,
    VariantProps<typeof tableVariants> {}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, variant, ...props }, ref) => (
    <div className="relative w-full overflow-auto">
      <table
        ref={ref}
        className={cn(tableVariants({ variant }), className)}
        {...props}
      />
    </div>
  ),
);
Table.displayName = "Table";

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
));
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
));
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "bg-muted/50 border-t font-medium last:[&>tr]:border-b-0",
      className,
    )}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "hover:bg-muted/20 data-[state=selected]:bg-muted h-[var(--table-row-height)] border-b",
      className,
    )}
    {...props}
  />
));
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "text-muted-foreground h-[var(--table-row-height)] px-[var(--table-cell-padding)] text-left align-middle font-medium first:pl-[var(--table-edge-padding)] last:pr-[var(--table-edge-padding)] [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className,
    )}
    {...props}
  />
));
TableHead.displayName = "TableHead";

interface TableSortableHeadProps extends Omit<
  React.ThHTMLAttributes<HTMLTableCellElement>,
  "aria-sort"
> {
  direction?: TableSortDirection;
}

const TableSortableHead = React.forwardRef<
  HTMLTableCellElement,
  TableSortableHeadProps
>(({ className, direction, ...props }, ref) => (
  <TableSortDirectionContext.Provider value={direction}>
    <TableHead
      {...props}
      ref={ref}
      aria-sort={direction}
      className={cn(
        "p-0 first:p-0 last:p-0 first:[&>button]:pl-[var(--table-edge-padding)] last:[&>button]:pr-[var(--table-edge-padding)]",
        className,
      )}
    />
  </TableSortDirectionContext.Provider>
));
TableSortableHead.displayName = "TableSortableHead";

type TableSortTriggerProps = Omit<
  ButtonProps,
  "nativeButton" | "render" | "type" | "variant"
>;

const TableSortTrigger = React.forwardRef<
  HTMLButtonElement,
  TableSortTriggerProps
>(({ className, ...props }, ref) => (
  <Button
    {...props}
    ref={ref}
    nativeButton
    type="button"
    variant="ghost"
    className={cn(
      "h-[var(--table-row-height)] w-full justify-start rounded-none px-[var(--table-cell-padding)] py-0 text-[length:inherit]",
      className,
    )}
  />
));
TableSortTrigger.displayName = "TableSortTrigger";

type TableSortIndicatorProps = Omit<
  React.ComponentPropsWithoutRef<"svg">,
  "aria-hidden" | "children"
>;

function TableSortIndicator(props: TableSortIndicatorProps) {
  const direction = React.useContext(TableSortDirectionContext);

  if (!direction) return null;

  const Icon = direction === "ascending" ? ArrowUpIcon : ArrowDownIcon;

  return <Icon {...props} aria-hidden="true" data-icon="inline-end" />;
}

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "p-[var(--table-cell-padding)] align-middle first:pl-[var(--table-edge-padding)] last:pr-[var(--table-edge-padding)] [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className,
    )}
    {...props}
  />
));
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("text-muted-foreground mt-4", className)}
    {...props}
  />
));
TableCaption.displayName = "TableCaption";

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableSortableHead,
  TableSortTrigger,
  TableSortIndicator,
  TableRow,
  TableCell,
  TableCaption,
};
